import * as React from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { viralTranscribe } from '@/api/client';

type Props = { disabled: boolean; onText: (text: string) => void; onBusy: (busy: boolean) => void };

export function ViralVoiceInput({ disabled, onText, onBusy }: Props) {
  const [phase, setPhase] = React.useState<'idle' | 'starting' | 'recording' | 'transcribing'>('idle');
  const [seconds, setSeconds] = React.useState(0);
  const [error, setError] = React.useState('');
  const recorder = React.useRef<MediaRecorder | null>(null);
  const stream = React.useRef<MediaStream | null>(null);
  const timer = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const controller = React.useRef<AbortController | null>(null);
  const epoch = React.useRef(0);
  const busyRef = React.useRef(false);
  const callbacks = React.useRef({ onText, onBusy });
  callbacks.current = { onText, onBusy };
  const release = React.useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
  }, []);
  React.useEffect(() => () => {
    epoch.current++;
    controller.current?.abort();
    if (recorder.current?.state === 'recording') recorder.current.stop();
    release();
    callbacks.current.onBusy(false);
  }, [release]);

  const start = async () => {
    if (busyRef.current || disabled) return;
    setError('');
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('当前浏览器不支持录音，请使用 HTTPS 或直接输入文字');
      return;
    }
    busyRef.current = true;
    callbacks.current.onBusy(true);
    const generation = ++epoch.current;
    setPhase('starting');
    try {
      const media = await navigator.mediaDevices.getUserMedia({audio: true});
      if (generation !== epoch.current) { media.getTracks().forEach(t => t.stop()); return; }
      stream.current = media;
      const mimeType = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'].find(t => MediaRecorder.isTypeSupported(t));
      const rec = new MediaRecorder(media, mimeType ? {mimeType} : undefined);
      recorder.current = rec;
      const chunks: Blob[] = [];
      let bytes = 0;
      let recordingError = '';
      rec.ondataavailable = event => {
        if (event.data.size) { chunks.push(event.data); bytes += event.data.size; }
        if (bytes > 8 * 1024 * 1024 && rec.state === 'recording') {
          recordingError = '录音过大，请缩短后重试'; rec.stop();
        }
      };
      rec.onerror = () => {
        recordingError = '录音中断，请检查麦克风后重试';
        if (rec.state === 'recording') rec.stop();
      };
      rec.onstop = async () => {
        release();
        if (generation !== epoch.current) return;
        const abort = new AbortController();
        controller.current = abort;
        const timeout = setTimeout(() => abort.abort(), 180000);
        try {
          if (recordingError) throw new Error(recordingError);
          const audio = new Blob(chunks, {type: rec.mimeType});
          if (!audio.size) throw new Error('录音为空，请重新录音');
          if (audio.size > 8 * 1024 * 1024) throw new Error('录音过大，请缩短后重试');
          setPhase('transcribing');
          const result = await viralTranscribe(audio, abort.signal);
          if (generation === epoch.current) callbacks.current.onText(result.text);
        } catch (err) {
          if (generation === epoch.current) setError(abort.signal.aborted ? '识别等待超时，请稍后重试' : err instanceof Error ? err.message : '语音识别失败');
        } finally {
          clearTimeout(timeout);
          if (generation === epoch.current) {
            busyRef.current = false; setPhase('idle'); callbacks.current.onBusy(false);
          }
        }
      };
      rec.start(1000);
      setPhase('recording'); setSeconds(0);
      const started = Date.now();
      timer.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - started) / 1000);
        setSeconds(Math.min(60, elapsed));
        if (elapsed >= 60 && rec.state === 'recording') rec.stop();
      }, 250);
    } catch (err) {
      release();
      if (generation !== epoch.current) return;
      busyRef.current = false; callbacks.current.onBusy(false); setPhase('idle');
      const name = err instanceof DOMException ? err.name : '';
      setError(name === 'NotAllowedError' ? '麦克风权限未开启，请允许录音或直接输入文字' : name === 'NotFoundError' ? '未找到麦克风，请直接输入文字' : '无法开始录音，请检查设备后重试');
    }
  };
  const recording = phase === 'recording';
  const label = recording ? '停止录音' : phase === 'transcribing' ? '语音识别中' : phase === 'starting' ? '等待麦克风权限' : '开始语音输入';
  return <div className="shrink-0">
    <Button type="button" variant={recording ? 'destructive' : 'outline'} size="icon" aria-label={label} title={label}
      disabled={disabled || phase === 'starting' || phase === 'transcribing'}
      onClick={() => recording ? recorder.current?.stop() : void start()}>
      {recording ? <Square className="h-4 w-4" /> : phase !== 'idle' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
    </Button>
    {recording && <span className="ml-2 text-xs" role="status">{seconds}/60 秒</span>}
    {phase === 'transcribing' && <span className="ml-2 text-xs" role="status">识别中…</span>}
    {error && <p role="alert" className="mt-1 max-w-52 text-xs text-destructive">{error}</p>}
  </div>;
}
